export type LogLevel = "error" | "warn" | null;

/*
 * Deliberately conservative.
 *
 * The obvious implementation — does the line contain "error" — paints "0
 * errors", "error_count=0" and "no errors found" red, and a log where the
 * healthy lines are red is worse than one with no colour at all. So nothing
 * here matches a bare word in running text. Every pattern is anchored to a
 * convention that means severity: a structured `level=` field, a bracketed
 * tag, a severity token at the start of the line, or a handful of strings
 * that only ever appear in a failure.
 *
 * Missing a line is the acceptable failure. The words themselves are still in
 * the output either way — the colour is emphasis on top of text that already
 * says it, not the only thing carrying the meaning.
 */

/** `level=error`, `level="error"`, `"level":"error"`, `severity=ERROR`. */
function structured(names: string): RegExp {
  return new RegExp(`\\b(?:level|severity|lvl)"?\\s*[=:]\\s*"?(?:${names})\\b`, "i");
}

/** `[error]`, `(ERROR)`, `<warn>` — a tag rather than a sentence. */
function tagged(names: string): RegExp {
  return new RegExp(`[[(<]\\s*(?:${names})\\s*[\\])>]`, "i");
}

/**
 * A severity token at the head of the line, allowing a leading timestamp.
 *
 * `ERROR: connection refused`, `2026-08-04T10:00:00Z ERROR ...`. Anchored so
 * `Retrying after ERROR` — a line about an error rather than one reporting it
 * — is left alone.
 */
function leading(names: string): RegExp {
  // The lookahead is the load-bearing part. `\b` would call the hyphen in
  // `installed error-handler@2.1.0` a boundary and paint that line red.
  return new RegExp(`^\\s*(?:\\S{6,40}\\s+){0,2}(?:${names})(?=[\\s:)\\]]|$)`, "i");
}

// Longest alternative first, so `warning` is not shadowed by `warn`.
const ERROR_NAMES = "fatal|panic|critical|crit|emerg|alert|error|err";
const WARN_NAMES = "warning|warn";

/** Strings that are a failure wherever they appear, with no ambiguity. */
const ERROR_ALWAYS = [
  /^\s*panic:/i,
  /^\s*traceback \(most recent call last\)/i,
  /^\s*unhandled (?:exception|rejection)/i,
  /\bsegmentation fault\b/i,
  /^\s*[\w.$]*(?:Error|Exception)\s*:/, // `TypeError: …`, `java.io.IOException: …`
];

/*
 * Compiled once. `classifyLine` runs per line over logs that reach thousands,
 * and rebuilding six regexes inside that loop is the kind of waste that only
 * shows up on the log somebody actually needed to read.
 */
const ERROR_PATTERNS = [structured(ERROR_NAMES), tagged(ERROR_NAMES), leading(ERROR_NAMES)];
const WARN_PATTERNS = [structured(WARN_NAMES), tagged(WARN_NAMES), leading(WARN_NAMES)];

export function classifyLine(line: string): LogLevel {
  if (ERROR_ALWAYS.some((pattern) => pattern.test(line))) return "error";

  if (ERROR_PATTERNS.some((pattern) => pattern.test(line))) return "error";
  if (WARN_PATTERNS.some((pattern) => pattern.test(line))) return "warn";

  return null;
}

/** Semantic tokens only — these have to be right in whatever theme is on. */
export const LEVEL_STYLES: Record<Exclude<LogLevel, null>, string> = {
  error: "text-danger",
  warn: "text-warn",
};

/*
 * A leading timestamp, lifted into its own column.
 *
 * Runtimes prefix almost every line with one, and left in place they push all
 * the actual messages to a ragged right where none of them line up. Pulled
 * into a fixed gutter, the messages start at the same column and the log
 * becomes scannable.
 *
 * Only the formats a container actually emits: ISO-8601 as Docker writes it,
 * with or without fractional seconds and zone, and the space-separated variant
 * Postgres and MySQL use. Anything else is left exactly as it came.
 */
const TIMESTAMP =
  /^(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:[.,]\d+)?(?:Z|[+-]\d{2}:?\d{2})?)\s+(.*)$/;

export interface LogLine {
  /** `HH:MM:SS`, or null when the line carried no timestamp. */
  time: string | null;
  text: string;
}

export function splitTimestamp(line: string): LogLine {
  const match = TIMESTAMP.exec(line);
  if (!match?.[1]) return { time: null, text: line };

  // Just the clock. The date is the same for almost every line in a window,
  // so spending gutter width on it buys nothing.
  const clock = /(\d{2}:\d{2}:\d{2})/.exec(match[1]);
  return { time: clock?.[1] ?? null, text: match[2] ?? "" };
}
